import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close,
  Email,
  Send,
  AttachFile,
  PictureAsPdf,
  Description,
  Delete,
  CloudUpload,
} from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { apiUrl } from "./LoginSignup";
import "./BookingMailModal.css";

const SENDER_EMAIL = "legal@havefaithsolutions.com";
const COMPANY_DISPLAY_NAME = "Have Faith Solutions Pvt. Ltd.";

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const BookingMailModal = ({
  open,
  booking,
  bookingId,
  onClose,
  onMailSent,
}) => {
  const userSession = useMemo(
    () => JSON.parse(localStorage.getItem("userSession") || "{}"),
    []
  );

  const authHeaders = useMemo(
    () => ({
      authorization: userSession.token,
      "user-role": userSession.user_role,
      "user-name": userSession.name,
    }),
    [userSession.token, userSession.user_role, userSession.name]
  );

  const fileInputRef = useRef(null);

  const [mailType, setMailType] = useState("agreement");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [attachInvoice, setAttachInvoice] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [bookingDocs, setBookingDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const companyName = booking?.company_name || "Valued Client";
  const contactPerson = booking?.contact_person || "Sir/Madam";
  const servicesText = Array.isArray(booking?.services)
    ? booking.services.join(", ")
    : booking?.services || "business advisory services";

  // Template defaults generator
  const getTemplateData = useCallback((type) => {
    if (type === "agreement") {
      return {
        subject: `Service Agreement for Signature – ${companyName} | ${COMPANY_DISPLAY_NAME}`,
        message: `Dear ${contactPerson},\n\nGreetings from ${COMPANY_DISPLAY_NAME}.\n\nPlease find attached the official Service Agreement pertaining to ${companyName} for ${servicesText}.\n\nWe request you to kindly review the agreement carefully, sign it, and return a signed copy at your earliest convenience.\n\nIf you have any questions, feel free to reply to this email.\n\nWarm regards,\n${COMPANY_DISPLAY_NAME} Team\n${SENDER_EMAIL}`,
        attachInvoice: false,
      };
    } else if (type === "invoice") {
      return {
        subject: `Tax Invoice – ${companyName} | ${COMPANY_DISPLAY_NAME}`,
        message: `Dear ${contactPerson},\n\nGreetings from ${COMPANY_DISPLAY_NAME}.\n\nThank you for your business. Please find attached the official Tax Invoice for your records and accounting purposes.\n\nShould you have any questions or require additional details, please feel free to contact us.\n\nWarm regards,\n${COMPANY_DISPLAY_NAME} Team\n${SENDER_EMAIL}`,
        attachInvoice: true,
      };
    } else {
      return {
        subject: `Regarding your registration – ${companyName} | ${COMPANY_DISPLAY_NAME}`,
        message: `Dear ${contactPerson},\n\nGreetings from ${COMPANY_DISPLAY_NAME}.\n\nPlease find the attached documents for your records and review.\n\nIf you have any questions or require further assistance, please reply directly to this email.\n\nWarm regards,\n${COMPANY_DISPLAY_NAME} Team\n${SENDER_EMAIL}`,
        attachInvoice: false,
      };
    }
  }, [companyName, contactPerson, servicesText]);

  // Reset or populate fields when modal opens or booking changes
  useEffect(() => {
    if (open && booking) {
      setTo(booking.email || "");
      setCc("");
      const initial = getTemplateData("agreement");
      setMailType("agreement");
      setSubject(initial.subject);
      setMessage(initial.message);
      setAttachInvoice(initial.attachInvoice);
      setSelectedDocIds([]);
      setNewFiles([]);
    }
  }, [open, booking, getTemplateData]);

  const handleTypeChange = (_, newType) => {
    if (!newType) return;
    setMailType(newType);
    const template = getTemplateData(newType);
    setSubject(template.subject);
    setMessage(template.message);
    setAttachInvoice(template.attachInvoice);
  };

  const handleToggleDoc = (docId) => {
    setSelectedDocIds((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setNewFiles((prev) => [...prev, ...files].slice(0, 5));
  };

  const handleRemoveNewFile = (index) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!to.trim()) {
      enqueueSnackbar("Recipient email (To) is required.", { variant: "warning" });
      return;
    }

    try {
      setSending(true);
      const formData = new FormData();
      formData.append("to", to.trim());
      if (cc.trim()) formData.append("cc", cc.trim());
      formData.append("mailType", mailType);
      formData.append("subject", subject.trim());
      formData.append("message", message.trim());
      formData.append("attachAgreement", "false");
      formData.append("attachInvoice", String(attachInvoice));
      formData.append("attachedDocIds", JSON.stringify(selectedDocIds));

      newFiles.forEach((file) => {
        formData.append("attachments", file);
      });

      const res = await axios.post(
        `${apiUrl}/booking/${bookingId}/send-mail`,
        formData,
        {
          headers: {
            ...authHeaders,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      enqueueSnackbar(
        res.data?.message || `Email sent successfully from ${SENDER_EMAIL}.`,
        { variant: "success" }
      );

      if (onMailSent) {
        onMailSent(res.data?.booking);
      }
      onClose();
    } catch (err) {
      console.error("Send mail error:", err);
      enqueueSnackbar(
        err.response?.data?.message || "Failed to send email. Please check configuration.",
        { variant: "error" }
      );
    } finally {
      setSending(false);
    }
  };

  // Fetch live uploaded documents for this booking
  useEffect(() => {
    if (!open || !bookingId) return;
    let isMounted = true;
    const fetchDocs = async () => {
      try {
        setLoadingDocs(true);
        const res = await axios.get(`${apiUrl}/booking/${bookingId}/documents`, {
          headers: authHeaders,
        });
        if (isMounted) {
          const docs = res.data?.documents || [];
          setBookingDocs(docs);
        }
      } catch (err) {
        console.warn("Failed to fetch booking documents in mail modal:", err.message);
        if (isMounted && Array.isArray(booking?.documents)) {
          const valid = booking.documents.filter((d) => d && d._id && d.fileName);
          setBookingDocs(valid);
        }
      } finally {
        if (isMounted) setLoadingDocs(false);
      }
    };
    fetchDocs();
    return () => {
      isMounted = false;
    };
  }, [open, bookingId, authHeaders, booking?.documents]);

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="md"
      fullWidth
      className="bmail-dialog"
    >
      {/* Branded Header */}
      <Box className="bmail-header-box">
        <Box>
          <Box className="bmail-header-title">
            <Box className="bmail-icon-bubble">
              <Email fontSize="medium" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Send Mail ({SENDER_EMAIL})
              </Typography>
              <Typography variant="caption" sx={{ color: "#cbd5e1" }}>
                Official Client Email Dispatch &amp; Attachment Manager
              </Typography>
            </Box>
          </Box>
          <Box className="bmail-booking-meta">
            <span className="bmail-meta-chip">
              Company: <strong>{companyName}</strong>
            </span>
            <span className="bmail-meta-chip">
              Client: <strong>{contactPerson}</strong>
            </span>
            <span className="bmail-meta-chip">
              Sender: <strong>{SENDER_EMAIL}</strong>
            </span>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={sending}
          sx={{ color: "#ffffff" }}
          size="small"
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        {/* Sender & Recipient Section */}
        <Box className="bmail-address-section">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box className="bmail-sender-pill">
              <span style={{ color: "#64748b", fontWeight: 600 }}>From:</span>
              <span className="bmail-sender-tag">Have Faith Solutions &lt;{SENDER_EMAIL}&gt;</span>
            </Box>
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              Sent securely via authenticated company SMTP
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <TextField
              label="To (Client Email)"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              size="small"
              required
              fullWidth
              sx={{ flex: 2, minWidth: 260 }}
              helperText={!booking?.email ? "No email on record. Enter recipient address." : ""}
            />
            <TextField
              label="CC (Optional)"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              size="small"
              placeholder="e.g. accounts@client.com"
              sx={{ flex: 1, minWidth: 200 }}
            />
          </Box>
        </Box>

        {/* Mail Type Segmented Tabs */}
        <Box className="bmail-type-tabs">
          <Tabs
            value={mailType}
            onChange={handleTypeChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab
              value="agreement"
              label="Service Agreement"
              icon={<PictureAsPdf fontSize="small" />}
              iconPosition="start"
              className="bmail-type-tab"
            />
            <Tab
              value="invoice"
              label="Tax Invoice"
              icon={<Description fontSize="small" />}
              iconPosition="start"
              className="bmail-type-tab"
            />
            <Tab
              value="custom"
              label="Other / Custom Mail"
              icon={<AttachFile fontSize="small" />}
              iconPosition="start"
              className="bmail-type-tab"
            />
          </Tabs>
        </Box>

        {/* Subject */}
        <TextField
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          size="small"
          fullWidth
          required
          sx={{ mb: 2 }}
        />

        {/* Message Body */}
        <TextField
          label="Message Body"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          multiline
          rows={6}
          fullWidth
          required
          sx={{ mb: 2 }}
        />

        {/* Attachments Section */}
        <Box className="bmail-attachments-card">
          <Box className="bmail-attachments-title">
            <AttachFile fontSize="small" sx={{ color: "#0B1E7A" }} />
            <span>Attachments for this Email</span>
          </Box>

          {/* Service Agreement guidance notice */}
          {mailType === "agreement" && (
            <Box className="bmail-agreement-guide">
              <PictureAsPdf fontSize="small" sx={{ color: "#2563eb", mt: 0.2 }} />
              <Typography variant="body2" sx={{ fontSize: "0.82rem", color: "#1e3a8a", fontWeight: 500 }}>
                Select your uploaded <strong>Service Agreement</strong> from the documents below to attach to this email.
              </Typography>
            </Box>
          )}

          {/* Optional Tax Invoice PDF for invoice mail */}
          {mailType === "invoice" && (
            <Box sx={{ mb: 1.5, pb: 1, borderBottom: "1px dashed #e2e8f0" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={attachInvoice}
                    onChange={(e) => setAttachInvoice(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                    Attach Generated Tax Invoice PDF
                  </Typography>
                }
              />
            </Box>
          )}

          {/* Uploaded Booking Documents Selection */}
          <Box sx={{ mt: 1, mb: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
              <Typography
                variant="caption"
                sx={{ color: "#475569", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}
              >
                Attach Uploaded Documents:
              </Typography>
              {bookingDocs.length > 0 && (
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  {selectedDocIds.length} of {bookingDocs.length} selected
                </Typography>
              )}
            </Box>

            {loadingDocs ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5, px: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  Loading uploaded documents...
                </Typography>
              </Box>
            ) : bookingDocs.length > 0 ? (
              <Box className="bmail-existing-docs-list">
                {bookingDocs.map((doc) => {
                  const isSelected = selectedDocIds.includes(doc._id);
                  const isAgreement =
                    doc.category === "Agreement" ||
                    (doc.fileName && /agreement/i.test(doc.fileName));
                  return (
                    <Box
                      key={doc._id}
                      onClick={() => handleToggleDoc(doc._id)}
                      className={`bmail-doc-item ${isSelected ? "selected" : ""} ${
                        isAgreement ? "is-agreement" : ""
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        size="small"
                        sx={{ p: 0.5, color: isSelected ? "#1d4ed8" : "#94a3b8" }}
                      />
                      <Box className="bmail-doc-info">
                        <Box className="bmail-doc-name-line">
                          <span className="bmail-doc-name" title={doc.fileName}>
                            {doc.fileName}
                          </span>
                          {doc.category && (
                            <span className={`bmail-doc-cat-tag ${isAgreement ? "cat-agreement" : ""}`}>
                              {doc.category}
                            </span>
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.72rem" }}>
                          {formatBytes(doc.fileSize)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box className="bmail-no-docs-box">
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  No documents uploaded for this booking yet. You can upload files below or add them in Booking Documents.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Upload New Files */}
          <Box sx={{ mt: 1.5 }}>
            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: "none" }}
              accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            />
            <Box className="bmail-upload-btn-box">
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ textTransform: "none", fontSize: "0.82rem" }}
              >
                Upload Additional File
              </Button>
              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                Max 5 files (PDF, images, Word, Excel)
              </Typography>
            </Box>

            {newFiles.length > 0 && (
              <Box className="bmail-new-files-list">
                {newFiles.map((file, idx) => (
                  <Chip
                    key={idx}
                    label={`${file.name} (${formatBytes(file.size)})`}
                    onDelete={() => handleRemoveNewFile(idx)}
                    deleteIcon={<Delete fontSize="small" />}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* Footer Actions */}
      <Box className="bmail-footer">
        <Box className="bmail-footer-info">
          <Typography variant="caption">
            Sender: <strong>{SENDER_EMAIL}</strong> &bull; Total Attachments:{" "}
            <strong>
              {(attachInvoice ? 1 : 0) +
                selectedDocIds.length +
                newFiles.length}
            </strong>
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={sending}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="bmail-send-btn"
            disabled={sending}
            onClick={handleSend}
            startIcon={
              sending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Send fontSize="small" />
              )
            }
          >
            {sending ? "Sending..." : `Send Mail from ${SENDER_EMAIL}`}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default BookingMailModal;
